import * as React from 'react';
import { FC } from 'react';

import { ISanityBlockContent, ISanityImage } from '@rbi-ctg/menu';
import { MarketingBasicTile } from 'components/marketing-basic-tile';
import {
  HorizontalPosition,
  MarketingTile,
  ModalPayloadType,
  OnShowModalType,
  TileSize,
} from 'components/marketing-tile';
import Modal, { ModalContent, ModalHeading, ModalSize } from 'components/modal';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { IMarketingTileFragment } from 'generated/sanity-graphql';
import useDialogModal from 'hooks/use-dialog-modal';
import useMediaQuery from 'hooks/use-media-query';

import { LeftContainer, QuadTileContainer, RightContainer, Section } from './styles';
import { IMarketingTileGroupProps } from './types';

const getAlignment = (val: string) => {
  return HorizontalPosition[val.toUpperCase()];
};

const Tile = ({
  tile,
  size,
  buttonPosition,
  onShowTermsModal,
}: {
  tile: IMarketingTileFragment;
  size: TileSize;
  buttonPosition: HorizontalPosition;
  onShowTermsModal?: OnShowModalType;
}) => {
  const backgroundImage = tile.backgroundImage?.locale?.app;
  const textBlock = tile.textBlock?.localeRaw;
  const buttonText = tile.buttonAction?.actionText?.locale;
  const buttonUrl = tile.buttonAction?.actionUrl?.locale || tile.buttonAction?.route;
  const termsText = tile.termsText?.localeRaw;
  const termsButtonText = tile.termsButton?.actionText?.locale;
  const termsButtonUrl = tile.termsButton?.actionUrl?.locale;
  return (
    <MarketingTile
      id={tile._id}
      tileSize={size}
      typographyBlock={textBlock}
      backgroundImage={(backgroundImage as ISanityImage) || undefined}
      buttonText={buttonText || ''}
      buttonUrl={buttonUrl || ''}
      termsText={termsText || ''}
      termsButtonText={termsButtonText || undefined}
      termsButtonUrl={termsButtonUrl || undefined}
      onShowTermsModal={onShowTermsModal}
      textPosition={getAlignment(tile.textAlignment || 'right')}
      hasLockedAspectRatio={tile.lockedAspectRatio || undefined}
      textColor={tile.textColor || undefined}
      buttonPosition={buttonPosition}
    />
  );
};

export interface ITermModalComponentProps {
  headingText?: string;
  contentBlock?: ISanityBlockContent[];
  modalAppearanceEventMessage: string;
  onDismiss: () => void;
}
export const TermModalComponent = ({
  headingText,
  contentBlock,
  onDismiss = () => {},
  modalAppearanceEventMessage,
}: ITermModalComponentProps) => {
  return (
    <Modal
      size={ModalSize.SMALL}
      onDismiss={onDismiss}
      mParticleEventData={{
        modalAppearanceEventMessage,
        modalMessage: undefined,
      }}
    >
      <ModalContent>
        <ModalHeading>{headingText}</ModalHeading>
        {contentBlock?.length && <SanityBlockRenderer content={contentBlock} />}
      </ModalContent>
    </Modal>
  );
};

/**
 * MarketingTileGroup purpose
 */
const MarketingTileGroup: FC<IMarketingTileGroupProps> = ({ item }) => {
  const isMobile = useMediaQuery('mobile');

  const [TermsModal, openTermsModal, pendingData, dismissTermsModal] = useDialogModal<
    ModalPayloadType,
    ITermModalComponentProps
  >({
    Component: TermModalComponent,
  });

  const onShowTermsModal: OnShowModalType = data => {
    openTermsModal(data);
  };
  if (item.tiles) {
    return (
      <div>
        <TermsModal
          headingText={pendingData?.heading}
          contentBlock={pendingData?.content}
          onDismiss={dismissTermsModal}
          modalAppearanceEventMessage="Marketing Tile: terms and conditions shown"
        />
        {item.tiles.map((tile, idx) => {
          if (!tile) {
            return null;
          }
          switch (tile.__typename) {
            case 'MarketingTile': {
              const key = `MarketingTile_${tile._id}`;
              // Always half size on mobile
              const size = isMobile ? TileSize.HALF : TileSize.FULL;
              const buttonPosition = getAlignment(tile?.buttonAlignment || 'left');
              return (
                <Section key={key}>
                  <Tile
                    buttonPosition={buttonPosition}
                    size={size}
                    tile={tile as IMarketingTileFragment}
                  />
                </Section>
              );
            }
            case 'MarketingTilePair': {
              const leftKey = `MarketingTile_left_${tile.left?._id || idx}`;
              const rightKey = `MarketingTile_right_${tile.right?._id || idx}`;
              const key = `MarketingTilePair_${leftKey}_${rightKey}`;
              // Always half size on when it's a pair
              const halfSize = TileSize.HALF;
              const leftButtonPosition = getAlignment(tile.left?.buttonAlignment || 'left');
              const rightButtonPosition = getAlignment(tile.right?.buttonAlignment || 'left');
              const left = tile.left && (
                <Tile
                  buttonPosition={leftButtonPosition}
                  onShowTermsModal={onShowTermsModal}
                  size={halfSize}
                  tile={tile.left as IMarketingTileFragment}
                />
              );
              const right = tile.right && (
                <Tile
                  buttonPosition={rightButtonPosition}
                  onShowTermsModal={onShowTermsModal}
                  size={halfSize}
                  tile={tile.right as IMarketingTileFragment}
                />
              );
              // On mobile the tiles should always be stacked
              if (isMobile) {
                return (
                  <>
                    <Section key={leftKey}>{left}</Section>
                    <Section key={rightKey}>{right}</Section>
                  </>
                );
              }
              // On desktop they are side by side
              return (
                <Section key={key}>
                  <LeftContainer>{left}</LeftContainer>
                  <RightContainer>{right}</RightContainer>
                </Section>
              );
            }
            case 'MarketingTileQuad': {
              const basicTiles = Object.keys(tile)?.map(key => {
                return (
                  key !== '__typename' &&
                  tile[key] && (
                    <MarketingBasicTile
                      key={key}
                      description={tile[key].description?.localeRaw}
                      image={tile[key].image?.locale?.app}
                      title={tile[key].title?.locale}
                    />
                  )
                );
              });
              return basicTiles?.length ? (
                <QuadTileContainer>{basicTiles}</QuadTileContainer>
              ) : null;
            }

            default:
              return null;
          }
        })}
      </div>
    );
  }
  return null;
};

export default MarketingTileGroup;
